import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { endpoints } from "../apiConfig";
import { getTheme, themeVars } from "../onlineThemes";
import ThemedBanner, { ORDER_CSS } from "../components/ThemedBanner";
import { IconCheck, IconClock, IconCalendar, IconPin, IconPhone, IconReceipt, IconBag, IconUser, IconBolt, IconSearch, categoryIcon } from "../icons";

const API = endpoints.onlinePlace.replace(/\/online\/place\.php$/, "");
const img = (p) => (p ? `${API}/${p}` : null);
const cents = (n) => Math.round(Number(n) * 100);
const money = (c) => `$${(c / 100).toFixed(2)}`;
const MAX_QTY = 20;

const defaultsFor = (item) => {
  const picks = {};
  item.options.forEach((g) => {
    if (g.multi_select) {
      const d = g.choices.filter((c) => c.is_default && c.is_available).map((c) => c.id);
      if (d.length) picks[g.id] = d;
    } else if (g.required) {
      const d = g.choices.find((c) => c.is_default && c.is_available) || g.choices.find((c) => c.is_available);
      if (d) picks[g.id] = d.id;
    }
  });
  return picks;
};
const resolve = (item, picks) => {
  let extra = 0; const ids = []; const parts = []; let missing = false;
  item.options.forEach((g) => {
    if (g.multi_select) {
      const chosen = Array.isArray(picks[g.id]) ? picks[g.id] : [];
      const cs = g.choices.filter((c) => chosen.includes(c.id));
      if (!cs.length) { if (g.required) missing = true; return; }
      cs.forEach((c) => { ids.push(c.id); extra += cents(c.price_delta); });
      parts.push(`${g.name}: ${cs.map((c) => c.label).join(", ")}`);
      return;
    }
    const c = g.choices.find((x) => x.id === picks[g.id]);
    if (!c) { if (g.required) missing = true; return; }
    ids.push(c.id); extra += cents(c.price_delta); parts.push(`${g.name}: ${c.label}`);
  });
  return { ids, extra, text: parts.join("; "), missing };
};

// Public online-ordering page. The restaurant's theme, banner and logo come from the menu payload.
// The customer never chooses how to pay: the restaurant's own setting decides (shown as one clear line).
export default function OrderPage() {
  const tenant = new URLSearchParams(window.location.search).get("tenant") || "";
  const [menu, setMenu] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [cart, setCart] = useState({});
  const [openId, setOpenId] = useState(null);
  const [picks, setPicks] = useState({});
  const [step, setStep] = useState("");
  const [f, setF] = useState({ name: "", phone: "", email: "", notes: "", website: "" });
  const [pickup, setPickup] = useState("asap");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [showHours, setShowHours] = useState(false);
  const [active, setActive] = useState("");
  const [q, setQ] = useState("");
  const secRefs = useRef({});

  useEffect(() => {
    if (!tenant) { setLoadError("This link is missing its restaurant."); return; }
    axios.get(endpoints.onlineMenu(tenant))
      .then((res) => {
        setMenu(res.data);
        setPickup(res.data.asap_available ? "asap" : "scheduled");
        const firstOpen = res.data.dates.find((d) => d.open);
        setDate(firstOpen ? firstOpen.date : "");
        if (res.data.categories[0]) setActive(String(res.data.categories[0].id));
      })
      .catch((err) => setLoadError(err.response?.data?.error || "We couldn't load this menu."));
  }, [tenant]);

  useEffect(() => {
    if (pickup !== "scheduled" || !date) return;
    setSlot("");
    axios.get(endpoints.onlineSlots(tenant, date)).then((res) => setSlots(res.data.slots)).catch(() => setSlots([]));
  }, [pickup, date, tenant]);

  useEffect(() => {
    if (!menu) return undefined;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const secs = menu.categories.map((c) => secRefs.current[c.id]).filter(Boolean);
        if (!secs.length) return;
        let cur = secs[0];
        secs.forEach((el) => { if (el.getBoundingClientRect().top <= 140) cur = el; });
        if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) cur = secs[secs.length - 1];
        setActive(cur.dataset.cat);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, [menu]);

  const theme = useMemo(() => getTheme(menu?.branding?.theme), [menu]);
  const lines = Object.values(cart);
  const count = lines.reduce((a, l) => a + l.qty, 0);
  const subtotal = lines.reduce((a, l) => a + (l.base + l.extra) * l.qty, 0);
  const tax = menu ? Math.round(subtotal * menu.tax_rate / 100) : 0;
  const total = subtotal + tax;
  const qtyOf = (id) => lines.filter((l) => l.id === id).reduce((a, l) => a + l.qty, 0);

  const bump = (item, r, d) => {
    const key = r.ids.length ? `i${item.id}|${[...r.ids].sort((a, b) => a - b).join(",")}` : `i${item.id}`; // "i" prefix keeps lines in the order they were added
    setCart((c) => {
      const qty = Math.min(MAX_QTY, (c[key]?.qty || 0) + d);
      const next = { ...c };
      if (qty <= 0) delete next[key]; else next[key] = { key, id: item.id, name: item.name, base: cents(item.price), ids: r.ids, extra: r.extra, text: r.text, qty };
      return next;
    });
  };
  const changeQty = (key, d) => setCart((c) => {
    const cur = c[key]; if (!cur) return c;
    const qty = cur.qty + d; const next = { ...c };
    if (qty <= 0) delete next[key]; else next[key] = { ...cur, qty: Math.min(MAX_QTY, qty) };
    return next;
  });
  const plainLine = (item) => lines.find((l) => l.id === item.id && l.ids.length === 0);

  if (loadError) return <Page theme={getTheme("generic")}><div className="op-wrap" style={{ paddingTop: 40 }}><div className="op-notice" role="alert" data-testid="load-error" style={{ background: "#fef3f2", color: "#b42318" }}>{loadError}</div></div></Page>;
  if (!menu) return <Page theme={getTheme("generic")}><ThemedBanner theme={getTheme("generic")} initials="" /><div className="op-wrap"><div className="op-head"><div className="op-skel" style={{ height: 34, width: "60%" }} /><div className="op-skel" /><div className="op-skel" /></div></div></Page>;

  const br = menu.branding;
  const stripe = menu.payment.method === "stripe";
  const overLimit = !stripe && menu.payment.store_limit !== null && total / 100 > menu.payment.store_limit;
  const belowMin = subtotal / 100 < menu.min_order;
  const canOrder = menu.accepting && (menu.asap_available || menu.scheduled_available);
  const payLine = stripe ? "You'll pay securely by card on the next page."
    : overLimit ? `Orders over $${menu.payment.store_limit.toFixed(0)} must be paid by phone before they go to the kitchen. We'll tell you how after you order.`
      : "You'll pay at the restaurant. We'll call to confirm your order.";

  const openOptions = (item) => { if (openId === item.id) { setOpenId(null); return; } setPicks(defaultsFor(item)); setOpenId(item.id); };
  const goto = (id) => { const el = secRefs.current[id]; if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); setActive(String(id)); };

  const place = async () => {
    setFormError("");
    if (pickup === "scheduled" && !slot) { setFormError("Choose a pickup time."); setStep("pickup"); return; }
    setBusy(true);
    try {
      const body = { tenant, name: f.name, phone: f.phone, email: f.email, notes: f.notes, website: f.website, pickup, items: lines.map((l) => ({ id: l.id, qty: l.qty, options: l.ids })) };
      if (pickup === "scheduled") body.pickup_at = `${date} ${slot}`;
      const res = await axios.post(endpoints.onlinePlace, body);
      if (res.data.checkout_url) { window.location.href = res.data.checkout_url; return; }
      sessionStorage.setItem(`remo_phone_${tenant}`, f.phone);
      window.location.href = `/track?tenant=${tenant}&order=${encodeURIComponent(res.data.order_number)}&placed=1`;
    } catch (err) {
      setFormError(err.response?.data?.error || "Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  const CartLines = () => lines.map((l) => (
    <div key={l.key} className="op-line-row">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div>{l.name}</div>{l.text && <div className="op-line-opt">{l.text}</div>}
        <div className="op-stepper" style={{ marginTop: 6 }}>
          <button type="button" aria-label={`Decrease ${l.name}`} onClick={() => changeQty(l.key, -1)}>−</button>
          <span>{l.qty}</span>
          <button type="button" className="pl" aria-label={`Increase ${l.name}`} onClick={() => changeQty(l.key, 1)}>+</button>
        </div>
      </div>
      <strong>{money((l.base + l.extra) * l.qty)}</strong>
    </div>
  ));
  const Totals = ({ tid }) => (
    <>
      <div className="op-tot"><span>Subtotal</span><span>{money(subtotal)}</span></div>
      <div className="op-tot"><span>Tax</span><span>{money(tax)}</span></div>
      <div className="op-tot" style={{ fontWeight: 700, fontSize: 17 }} data-testid={tid}><span>Total</span><span>{money(total)}</span></div>
    </>
  );
  const stepIndex = { pickup: 0, details: 1, review: 2 }[step];
  const searching = q.trim().length > 0;
  const visibleCategories = searching
    ? [{ id: "search", name: `Search results`, items: menu.categories.flatMap((c) => c.items).filter((i) => i.name.toLowerCase().includes(q.trim().toLowerCase()) || (i.description || "").toLowerCase().includes(q.trim().toLowerCase())) }]
    : menu.categories;

  return (
    <Page theme={theme}>
      <div className="op-stickyhead">
        <ThemedBanner theme={theme} bannerUrl={img(br.banner)} logoUrl={img(br.logo)} initials={br.initials} />
        <div className="op-tabs" role="tablist" aria-label="Menu categories">
          {menu.categories.map((c) => <button key={c.id} role="tab" aria-selected={active === String(c.id)} className={`op-tab${active === String(c.id) ? " on" : ""}`} onClick={() => goto(c.id)}>{c.name}</button>)}
        </div>
      </div>
      <div className="op-search">
        <IconSearch width={16} height={16} />
        <input type="search" placeholder="Search the menu" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the menu" data-testid="menu-search" />
      </div>
      <div className="op-wrap">
        <header className="op-head">
          <div>
            <h1 className="op-name" data-testid="restaurant-name">{menu.business_name}</h1>
            {br.tagline && <p className="op-tag">{br.tagline}</p>}
          </div>
          <div className="op-chips">
            <span className={`op-chip ac${menu.open_now ? " ok" : " off"}`}><IconCheck />{menu.open_now ? "Open now" : "Closed right now"}</span>
            {menu.asap_available && <span className="op-chip ac"><IconClock />Pickup in about {menu.prep_minutes} min</span>}
            <span style={{ position: "relative", display: "inline-flex" }}>
              <button type="button" className="op-chip ac" onClick={() => setShowHours(!showHours)} style={{ cursor: "pointer", fontFamily: "inherit" }}><IconCalendar />{showHours ? "Hide hours" : "Hours"}</button>
              {showHours && (
                <div className="op-hours-pop" style={{ background: "var(--card)", border: "1px solid var(--ln)" }} data-testid="hours-popover">
                  {menu.hours.map((h) => <div key={h.day} className="op-hours-row"><span>{h.day}</span><span>{h.text}</span></div>)}
                </div>
              )}
            </span>
            {menu.address && <a className="op-chip ac" href={menu.address.maps_url} target="_blank" rel="noreferrer"><IconPin />Directions</a>}
            <a className="op-chip ac" href={`tel:${menu.phone.replace(/\D/g, "")}`}><IconPhone />{menu.phone}</a>
            <a className="op-chip ac" href={`/track?tenant=${tenant}`}><IconReceipt />Track an order</a>
          </div>
        </header>
        {menu.pickup_notes && <div className="op-notice" style={{ background: "var(--card)", border: "1px dashed var(--ac)" }}>{menu.pickup_notes}</div>}
        {menu.test_mode && <div className="op-notice" style={{ background: "#fffaeb", color: "#93370d", fontWeight: 600 }} data-testid="test-banner">TEST MODE: no real charges. Use Stripe's test card 4242 4242 4242 4242.</div>}
        {!canOrder && <div className="op-notice" role="alert" data-testid="closed-banner" style={{ background: "#fef3f2", color: "#b42318" }}>{menu.unavailable_reason === "payments_unavailable" ? "Online ordering is unavailable right now." : "The restaurant isn't taking online orders right now."}</div>}
        {canOrder && !menu.asap_available && !menu.open_now && <div className="op-notice" style={{ background: "#fffaeb", color: "#93370d" }}>The restaurant is closed at the moment. You can still schedule a pickup for later.</div>}

        <div className="op-cols">
          <nav className="op-cats" aria-label="Categories" data-testid="category-sidebar">
            <div className="op-cats-inner">
              <div className="op-cats-label">Menu</div>
              {menu.categories.map((c) => {
                const Ic = categoryIcon(c.name);
                return (
                  <button key={c.id} type="button" className={active === String(c.id) ? "on" : ""} onClick={() => goto(c.id)}>
                    <span className="op-cat-ic"><Ic width={14} height={14} /></span>
                    {c.name}<span className="op-cat-n">{c.items.length}</span>
                  </button>
                );
              })}
            </div>
          </nav>
          <div>
            {visibleCategories.map((c) => (
              <section key={c.id} className="op-sec" data-cat={c.id} ref={(el) => { if (!searching) secRefs.current[c.id] = el; }}>
                <h2>{c.name}</h2>
                <div className="op-grid">
                {c.items.map((item) => {
                  const inQty = qtyOf(item.id); const hasOpts = item.options.length > 0; const pl = plainLine(item);
                  const r = resolve(item, picks);
                  const photo = img(item.image);
                  const addBtn = hasOpts
                    ? <button className="op-add" aria-label={`Add ${item.name}`} aria-expanded={openId === item.id} disabled={!canOrder} onClick={() => openOptions(item)}>{openId === item.id ? "×" : "+"}</button>
                    : (!pl && <button className="op-add" aria-label={`Add ${item.name}`} disabled={!canOrder} onClick={() => bump(item, { ids: [], extra: 0, text: "" }, 1)}>+</button>);
                  return (
                    <div key={item.id} className={`op-item${inQty ? " in" : ""}${photo ? "" : " nophoto"}`} data-testid="menu-item">
                      <div className={`op-media ${photo ? "has" : "none"}`} style={photo ? { backgroundImage: `url(${photo})` } : undefined} data-testid={photo ? "item-photo" : undefined}>
                        {!photo && <span className="op-letter" aria-hidden="true">{(item.name.trim()[0] || "").toUpperCase()}</span>}
                        {inQty > 0 && hasOpts && <span className="op-badge">{inQty}</span>}
                        {addBtn}
                      </div>
                      <div className="op-item-main">
                        <div className="op-item-name">{item.name}</div>
                        {item.description && <div className="op-item-desc">{item.description}</div>}
                        <div className="op-price">{money(cents(item.price))}</div>
                        {!hasOpts && pl && (
                          <div className="op-stepper" style={{ marginTop: 8, alignSelf: "flex-start" }}>
                            <button type="button" aria-label={`Remove one ${item.name}`} onClick={() => bump(item, { ids: [], extra: 0, text: "" }, -1)}>−</button>
                            <span>{pl.qty}</span>
                            <button type="button" className="pl" aria-label={`Add one ${item.name}`} onClick={() => bump(item, { ids: [], extra: 0, text: "" }, 1)}>+</button>
                          </div>
                        )}
                        {hasOpts && openId === item.id && (
                          <div className="op-opts" data-testid="options-panel">
                            {item.options.map((g) => (
                              <div key={g.id}>
                                <label htmlFor={`g${item.id}-${g.id}`}>{g.name}{g.required ? "" : " (optional)"}{g.multi_select ? " — choose any number" : ""}</label>
                                {g.multi_select ? (
                                  <div role="group" aria-label={g.name} style={{ display: "grid", gap: 6 }}>
                                    {g.choices.map((c) => {
                                      const checked = Array.isArray(picks[g.id]) && picks[g.id].includes(c.id);
                                      return (
                                        <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, opacity: c.is_available ? 1 : 0.55 }}>
                                          <input type="checkbox" checked={checked} disabled={!c.is_available} onChange={(e) => {
                                            const cur = Array.isArray(picks[g.id]) ? picks[g.id] : [];
                                            setPicks({ ...picks, [g.id]: e.target.checked ? [...cur, c.id] : cur.filter((x) => x !== c.id) });
                                          }} />
                                          {c.label}{c.price_delta > 0 ? ` (+${money(cents(c.price_delta))})` : ""}{!c.is_available ? " (sold out)" : ""}
                                        </label>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <select id={`g${item.id}-${g.id}`} className="op-select" value={picks[g.id] || ""} onChange={(e) => setPicks({ ...picks, [g.id]: e.target.value ? Number(e.target.value) : undefined })} aria-label={g.name}>
                                    {!g.required && <option value="">No thanks</option>}
                                    {g.choices.map((c) => <option key={c.id} value={c.id} disabled={!c.is_available}>{c.label}{c.price_delta > 0 ? ` (+${money(cents(c.price_delta))})` : ""}{!c.is_available ? " (sold out)" : ""}</option>)}
                                  </select>
                                )}
                              </div>
                            ))}
                            <button className="op-btn" disabled={r.missing} aria-label={`Add ${item.name} to order`} onClick={() => { bump(item, r, 1); setOpenId(null); }}>Add to order · {money(cents(item.price) + r.extra)}</button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                </div>
              </section>
            ))}
            {visibleCategories.length === 0 || (searching && visibleCategories[0].items.length === 0) ? <p style={{ color: "var(--mu)" }} data-testid="search-empty">{searching ? "No items match your search." : "The menu isn't available yet."}</p> : null}
            <p className="op-powered">Powered by Alvna</p>
          </div>

          <aside className="op-side" data-testid="side-cart">
            <div className="op-side-inner">
              <h3 style={{ fontFamily: "var(--hf)", fontWeight: "var(--hw)", margin: "0 0 8px" }}>Your order</h3>
              {lines.length === 0 ? <p style={{ color: "var(--mu)", fontSize: 14 }}>Your order is empty. Add something tasty.</p> : (<><CartLines /><Totals />{belowMin && <div className="op-err">The minimum online order is ${menu.min_order.toFixed(2)}.</div>}<button className="op-btn" style={{ width: "100%", marginTop: 10 }} disabled={belowMin || !canOrder} onClick={() => setStep("pickup")}>Checkout · {money(total)}</button></>)}
            </div>
          </aside>
        </div>
      </div>

      {count > 0 && !step && (
        <div className="op-bar">
          <button className="op-btn" onClick={() => setStep("cart")} data-testid="view-order"><span>View order ({count})</span><span>{money(total)}</span></button>
        </div>
      )}

      {step === "cart" && (
        <div className="op-sheet-bg" onClick={() => setStep("")}>
          <div className="op-sheet" role="dialog" aria-label="Your order" onClick={(e) => e.stopPropagation()}>
            <div className="op-sheet-body">
              <h3>Your order</h3>
              <CartLines /><Totals />
              {belowMin && <div className="op-err">The minimum online order is ${menu.min_order.toFixed(2)}.</div>}
              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                <button className="op-btn ghost" onClick={() => setStep("")}>Keep browsing</button>
                <button className="op-btn" style={{ flex: 1 }} disabled={belowMin || !canOrder || count === 0} onClick={() => setStep("pickup")}>Checkout</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {stepIndex !== undefined && (
        <div className="op-sheet-bg">
          <div className="op-sheet" role="dialog" aria-label="Checkout" data-testid="checkout" onClick={(e) => e.stopPropagation()}>
            <div className="op-sheet-head">
              <h3>Checkout</h3>
              <div className="op-steps-ic" aria-label={`Step ${stepIndex + 1} of 3`}>
                {[["Pickup", IconBag], ["Details", IconUser], ["Review", IconReceipt]].map(([label, Ic], i) => (
                  <React.Fragment key={label}>
                    <div className={`op-step${i <= stepIndex ? " on" : ""}`}><span className="op-step-circle"><Ic width={14} height={14} /></span><span className="op-step-label">{label}</span></div>
                    {i < 2 && <div className="op-step-line" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="op-sheet-body">
            <button type="button" style={{ background: "none", border: "none", color: "var(--ac)", cursor: "pointer", fontSize: 14, padding: 0, marginBottom: 8, fontFamily: "inherit" }} onClick={() => setStep(stepIndex === 0 ? "cart" : ["pickup", "details"][stepIndex - 1])}>← Back</button>

            {step === "pickup" && (
              <>
                <h3>When do you want it?</h3>
                {menu.asap_available && <label className={`op-choice${pickup === "asap" ? " on" : ""}`}><input type="radio" name="pickup" checked={pickup === "asap"} onChange={() => setPickup("asap")} style={{ position: "absolute", opacity: 0, width: 1, height: 1 }} /><span className="op-choice-ic"><IconBolt width={15} height={15} /></span> ASAP, about {menu.prep_minutes} min</label>}
                {menu.scheduled_available && <label className={`op-choice${pickup === "scheduled" ? " on" : ""}`}><input type="radio" name="pickup" checked={pickup === "scheduled"} onChange={() => setPickup("scheduled")} style={{ position: "absolute", opacity: 0, width: 1, height: 1 }} /><span className="op-choice-ic"><IconCalendar width={15} height={15} /></span> Schedule for later</label>}
                {pickup === "scheduled" && (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <select className="op-select" style={{ flex: "1 1 140px" }} value={date} onChange={(e) => setDate(e.target.value)} aria-label="Pickup day">{menu.dates.filter((d) => d.open).map((d) => <option key={d.date} value={d.date}>{d.label}</option>)}</select>
                    <select className="op-select" style={{ flex: "1 1 120px" }} value={slot} onChange={(e) => setSlot(e.target.value)} aria-label="Pickup time">
                      <option value="">{slots.length ? "Choose a time" : "No times left"}</option>
                      {slots.filter((s) => s.available).map((s) => <option key={s.time} value={s.time}>{new Date(`2000-01-01T${s.time}`).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</option>)}
                    </select>
                  </div>
                )}
                {formError && <div className="op-err" role="alert" data-testid="form-error">{formError}</div>}
                <button className="op-btn" style={{ width: "100%", marginTop: 14 }} onClick={() => { if (pickup === "scheduled" && !slot) { setFormError("Choose a pickup time."); return; } setFormError(""); setStep("details"); }}>Continue</button>
              </>
            )}

            {step === "details" && (
              <form onSubmit={(e) => { e.preventDefault(); setStep("review"); }}>
                <h3>Your details</h3>
                <input className="op-field" placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required maxLength={80} aria-label="Your name" />
                <input className="op-field" placeholder="Phone number" inputMode="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} required aria-label="Phone number" />
                {!stripe && <p className="op-hint">Make sure this number is correct. The restaurant may call it to confirm your order.</p>}
                <input className="op-field" placeholder="Email (optional, for a confirmation)" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} aria-label="Email" />
                <textarea className="op-field" style={{ minHeight: 64 }} placeholder="Notes for the kitchen (optional)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} maxLength={500} aria-label="Notes" />
                <input style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} name="website" />
                <button className="op-btn" style={{ width: "100%" }} type="submit">Continue</button>
              </form>
            )}

            {step === "review" && (
              <>
                <h3>Review your order</h3>
                <CartLines /><Totals tid="total" />
                <div className="op-line-row" style={{ borderBottom: "none" }}><span>Pickup</span><strong>{pickup === "asap" ? `ASAP · about ${menu.prep_minutes} min` : `${menu.dates.find((d) => d.date === date)?.label || date} · ${slot}`}</strong></div>
                <div className="op-payline" data-testid="pay-line">{payLine}</div>
                {formError && <div className="op-err" role="alert" data-testid="form-error">{formError}</div>}
                <button className="op-btn" style={{ width: "100%" }} disabled={busy || belowMin || !canOrder} onClick={place}>{busy ? "Placing your order…" : stripe ? `Pay ${money(total)}` : `Place order · ${money(total)}`}</button>
              </>
            )}
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}

function Page({ theme, children }) {
  return (
    <div className="op-root" style={themeVars(theme)}>
      <style>{ORDER_CSS}</style>
      {children}
    </div>
  );
}
